// components/ImageEditor/ImageEditorModal.jsx
import React, { useState, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { motion } from 'framer-motion';

const ImageEditorModal = ({ 
  isOpen, 
  onClose, 
  imageSrc, 
  onSave, 
  aspectRatio = 1,
  type = 'profile',
  allowCropping = true,
  showSkipButton = false,
  onSkip = null
}) => {
  const [crop, setCrop] = useState({ 
    unit: '%', 
    width: 90, 
    height: 90, 
    aspect: aspectRatio 
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // Get cropped image
  const getCroppedImg = useCallback(async () => {
    if (!imageRef || !completedCrop) {
      return null;
    }
  
    const canvas = document.createElement('canvas');
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;
    
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      imageRef,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // Fallback with lower quality if first attempt fails
            canvas.toBlob(
              (fallbackBlob) => resolve(fallbackBlob),
              'image/jpeg',
              0.7
            );
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  }, [imageRef, completedCrop]);

  // Get original image as blob (when skipping cropping)
  const getOriginalImageBlob = useCallback(async () => {
    if (!imageSrc) return null;
    
    try {
      // If it's already a blob URL or data URL, convert it to blob
      if (imageSrc.startsWith('blob:') || imageSrc.startsWith('data:')) {
        const response = await fetch(imageSrc);
        return await response.blob();
      } else {
        // If it's a regular URL, fetch it
        const response = await fetch(imageSrc);
        return await response.blob();
      }
    } catch (error) {
      console.error('Error getting original image blob:', error);
      return null;
    }
  }, [imageSrc]);

  const handleSave = async () => {
    try {
      let imageBlob = null;
      let imageUrl = imageSrc;

      // Check if user made any changes
      const hasChanges = completedCrop && (
        zoom !== 1 || 
        rotation !== 0 || 
        completedCrop.width < 100 || 
        completedCrop.height < 100 ||
        completedCrop.x > 0 || 
        completedCrop.y > 0
      );

      console.log('Save clicked - hasChanges:', hasChanges, 'completedCrop:', completedCrop);

      if (hasChanges && allowCropping) {
        // Apply cropping and transformations
        console.log('Applying cropping and transformations...');
        imageBlob = await getCroppedImg();
        console.log('Cropped image blob:', imageBlob);
        
        if (imageBlob) {
          imageUrl = URL.createObjectURL(imageBlob);
          console.log('Created blob URL from cropped image');
        }
      } else {
        // Use original image without changes
        console.log('Using original image without changes');
        imageBlob = await getOriginalImageBlob();
        console.log('Original image blob:', imageBlob);
      }

      if (imageUrl) {
        console.log('Calling onSave with imageUrl and imageBlob');
        onSave(imageUrl, imageBlob);
      } else {
        console.error('No image URL available, using fallback');
        // Fallback: if everything fails, just pass the original source
        onSave(imageSrc, null);
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      // Fallback in case of any error
      onSave(imageSrc, null);
    }
    onClose();
  };

  const handleSkip = async () => {
    if (onSkip) {
      onSkip();
    } else {
      // Default skip behavior: use original image
      const imageBlob = await getOriginalImageBlob();
      onSave(imageSrc, imageBlob);
      onClose();
    }
  };

  // Reset state when modal opens with new image
  React.useEffect(() => {
    if (isOpen && imageSrc) {
      setCrop({ 
        unit: '%', 
        width: 90, 
        height: 90, 
        aspect: aspectRatio 
      });
      setCompletedCrop(null);
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen, imageSrc, aspectRatio]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-1 md:p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-4 md:p-6 w-[90%] md:w-full md:max-w-3xl md:h-auto"
      >
        <h2 className="md:text-2xl text-[10px] font-bold mb-1 md:mb-4">
          {allowCropping ? 'Edit Image' : 'Preview Image'}
        </h2>
        
        <div className="flex flex-col md:flex-row gap-2 md:gap-6">
          <div className="flex-1">
            <div className="relative border rounded-md overflow-hidden bg-gray-100">
              {allowCropping ? (
                <ReactCrop
                  crop={crop}
                  onChange={(newCrop) => setCrop(newCrop)}
                  onComplete={(c) => {
                    console.log('Crop completed:', c);
                    setCompletedCrop(c);
                  }}
                  aspect={aspectRatio}
                  className="max-h-[400px]"
                >
                  <img
                    ref={setImageRef}
                    src={imageSrc}
                    style={{ 
                      transform: `scale(${zoom}) rotate(${rotation}deg)`,
                      maxHeight: '400px'
                    }}
                    alt="Crop preview"
                    onLoad={(e) => {
                      console.log('Image loaded, setting initial crop');
                      setCrop({
                        unit: '%',
                        width: 90,
                        height: 90,
                        x: 5,
                        y: 5,
                        aspect: aspectRatio
                      });
                    }}
                  />
                </ReactCrop>
              ) : (
                <img
                  ref={setImageRef}
                  src={imageSrc}
                  style={{ 
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    maxHeight: '400px',
                    width: '100%',
                    height: 'auto'
                  }}
                  alt="Image preview"
                />
              )}
            </div>

            {allowCropping && (
              <div className="mt-2 md:mt-4 space-y-1 md:space-y-4">
                <div>
                  <label className="block text-[8px] md:text-sm font-medium md:mb-1">
                    Zoom: {Math.round(zoom * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-[8px] md:text-sm font-medium md:mb-1">
                    Rotation: {rotation}°
                  </label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    step="1"
                    value={rotation}
                    onChange={(e) => setRotation(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="md:w-64 flex flex-col gap-1 md:gap-4">
            <h3 className="font-semibold md:text-2xl text-[8px]">Preview</h3>
            <div 
              className="border rounded-md overflow-hidden bg-gray-100 flex items-center justify-center"
              style={{
                width: '100%',
                height: type === 'profile' ? '200px' : '100px',
                aspectRatio: aspectRatio
              }}
            >
              <img
                src={imageSrc}
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                alt="Preview"
              />
            </div>

            <div className="text-[8px] md:text-sm text-gray-600">
              <p>This is how your image will appear</p>
              {allowCropping && (
                <>
                  <p>Drag to reposition the image</p>
                  <p>Use zoom and rotation to adjust</p>
                </>
              )}
              <p>Click Save to use this image</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-2 md:mt-6">
          {showSkipButton && (
            <button
              onClick={handleSkip}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-[8px] md:text-sm"
            >
              Skip Editing
            </button>
          )}
          
          <button
            onClick={onClose}
            className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-[8px] md:text-sm"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            className="px-3 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 text-[8px] md:text-sm"
          >
            Save Image
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ImageEditorModal;